import { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import DictationInput from '../../components/DictationInput';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { 
  Users, Search, Send, CheckCircle, XCircle, Clock, 
  Briefcase, GraduationCap, Heart, Home, MessageSquare, 
  RefreshCw, Award, AlertCircle 
} from 'lucide-react';
import { formatDate, ROLE_LABELS } from '../../lib/utils';
import { toast } from 'sonner';

const NEED_CATEGORIES = [
  { value: 'employment', label: 'Employment', icon: Briefcase, color: 'bg-emerald-50 text-emerald-700' },
  { value: 'education', label: 'Education/Training', icon: GraduationCap, color: 'bg-sky-50 text-sky-700' },
  { value: 'housing', label: 'Housing', icon: Home, color: 'bg-violet-50 text-violet-700' },
  { value: 'healthcare', label: 'Healthcare', icon: Heart, color: 'bg-rose-50 text-rose-700' },
  { value: 'general', label: 'General Support', icon: Users, color: 'bg-slate-100 text-slate-700' },
];

export default function CommunityPartnerPortal() {
  const { user, axiosAuth } = useAuth();
  const [needs, setNeeds] = useState([]);
  const [myRequests, setMyRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [categoryFilter, setCategoryFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedNeed, setSelectedNeed] = useState(null);
  const [connectDialogOpen, setConnectDialogOpen] = useState(false);
  const [connectMessage, setConnectMessage] = useState('');
  const [resourceOffered, setResourceOffered] = useState('');
  const api = axiosAuth();

  const fetchData = async () => {
    setLoading(true);
    try {
      const [needsRes, requestsRes] = await Promise.all([
        api.get(`/partner/participant-needs${categoryFilter ? `?category=${categoryFilter}` : ''}`),
        api.get('/connections/partner/mine')
      ]);
      setNeeds(needsRes.data || []);
      setMyRequests(requestsRes.data || []);
    } catch (err) {
      toast.error('Failed to load partner data');
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchData();
  }, [categoryFilter]);

  const handleConnect = async () => {
    if (!selectedNeed) return;
    try {
      await api.post(`/connections/request?need_id=${selectedNeed.id}&message=${encodeURIComponent(connectMessage)}&resource_offered=${encodeURIComponent(resourceOffered)}`);
      toast.success('Connection request sent! A staff member will review it.');
      setConnectDialogOpen(false);
      setConnectMessage('');
      setResourceOffered('');
      setSelectedNeed(null);
      fetchData();
    } catch (err) {
      toast.error('Failed to send request');
    }
  };

  const openConnectDialog = (need) => {
    setSelectedNeed(need);
    setConnectDialogOpen(true);
  };

  const getCategoryInfo = (cat) => NEED_CATEGORIES.find(c => c.value === cat) || NEED_CATEGORIES[4];

  const getStatusBadge = (status) => {
    switch (status) {
      case 'pending_approval': return <Badge className="bg-amber-100 text-amber-700"><Clock size={12} className="mr-1" /> Pending Review</Badge>;
      case 'approved': return <Badge className="bg-sky-100 text-sky-700"><CheckCircle size={12} className="mr-1" /> Awaiting Response</Badge>;
      case 'accepted': return <Badge className="bg-emerald-100 text-emerald-700"><CheckCircle size={12} className="mr-1" /> Accepted</Badge>;
      case 'rejected': return <Badge className="bg-red-100 text-red-700"><XCircle size={12} className="mr-1" /> Rejected</Badge>;
      case 'declined': return <Badge className="bg-slate-100 text-slate-600"><XCircle size={12} className="mr-1" /> Declined</Badge>;
      default: return <Badge variant="outline">{status}</Badge>;
    }
  };

  const filteredNeeds = needs.filter(n => 
    !searchQuery || 
    n.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    n.description?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    n.participant_skills?.some(s => s.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-3 border-emerald-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="community-partner-portal">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-foreground" style={{ fontFamily: 'Manrope' }}>
            Community Partner Portal
          </h1>
          <p className="text-muted-foreground mt-1 text-sm">
            Connect with participants and offer resources
          </p>
        </div>
        <Button onClick={fetchData} variant="outline" className="gap-2 rounded-full" disabled={loading}>
          <RefreshCw size={16} className={loading ? 'animate-spin' : ''} /> Refresh
        </Button>
      </div>

      {/* Info Banner */}
      <Card className="border-0 shadow-sm rounded-2xl bg-gradient-to-r from-emerald-50 to-teal-50 border-l-4 border-l-emerald-500">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <AlertCircle className="text-emerald-600 shrink-0 mt-0.5" size={20} />
            <div>
              <p className="font-medium text-emerald-900">How Connection Requests Work</p>
              <p className="text-sm text-emerald-700 mt-1">
                When you send a connection request, it's first reviewed by the participant's assigned peer support specialist. 
                If approved, the participant can then choose to accept or decline. All communications are visible to program staff for safety and oversight.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      <Tabs defaultValue="needs" className="space-y-4">
        <TabsList className="bg-muted/50 rounded-full p-1">
          <TabsTrigger value="needs" className="rounded-full data-[state=active]:bg-white data-[state=active]:shadow-sm px-4">
            Participant Needs ({needs.length})
          </TabsTrigger>
          <TabsTrigger value="requests" className="rounded-full data-[state=active]:bg-white data-[state=active]:shadow-sm px-4">
            My Requests ({myRequests.length})
          </TabsTrigger>
        </TabsList>

        {/* Participant Needs Tab */}
        <TabsContent value="needs" className="space-y-4">
          {/* Filters */}
          <Card className="border-0 shadow-sm rounded-2xl">
            <CardContent className="p-4">
              <div className="flex flex-col md:flex-row gap-3">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={16} />
                  <Input 
                    placeholder="Search needs, skills..." 
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-9 rounded-xl"
                  />
                </div>
                <Select value={categoryFilter || 'all'} onValueChange={(v) => setCategoryFilter(v === 'all' ? '' : v)}>
                  <SelectTrigger className="w-full md:w-48 rounded-xl">
                    <SelectValue placeholder="All Categories" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Categories</SelectItem>
                    {NEED_CATEGORIES.map(cat => (
                      <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          {/* Needs Grid */}
          {filteredNeeds.length === 0 ? (
            <Card className="border-0 shadow-sm rounded-2xl">
              <CardContent className="p-8 text-center">
                <Users className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
                <p className="text-muted-foreground">No participant needs found matching your criteria.</p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid md:grid-cols-2 gap-4">
              {filteredNeeds.map(need => {
                const catInfo = getCategoryInfo(need.category);
                const CatIcon = catInfo.icon;
                const alreadyRequested = myRequests.some(r => r.need_id === need.id);
                return (
                  <Card key={need.id} className="border-0 shadow-sm rounded-2xl hover:shadow-md transition-shadow">
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between gap-3 mb-3">
                        <div className="flex items-center gap-2">
                          <div className={`w-10 h-10 rounded-xl ${catInfo.color.split(' ')[0]} flex items-center justify-center`}>
                            <CatIcon className={catInfo.color.split(' ')[1]} size={20} />
                          </div>
                          <div>
                            <h3 className="font-semibold">{need.title}</h3>
                            <Badge variant="outline" className="text-xs mt-0.5">{catInfo.label}</Badge>
                          </div>
                        </div>
                        {alreadyRequested ? (
                          <Badge className="bg-slate-100 text-slate-600">Requested</Badge>
                        ) : (
                          <Button 
                            size="sm" 
                            onClick={() => openConnectDialog(need)}
                            className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-full gap-1"
                            data-testid={`connect-btn-${need.id}`}
                          >
                            <Send size={14} /> Connect
                          </Button>
                        )}
                      </div>
                      {need.description && (
                        <p className="text-sm text-muted-foreground mb-3 line-clamp-2">{need.description}</p>
                      )}
                      {/* Participant Skills */}
                      {need.participant_skills?.length > 0 && (
                        <div className="mb-3">
                          <p className="text-xs font-medium text-muted-foreground mb-1.5 flex items-center gap-1">
                            <Award size={12} /> Skills & Experience
                          </p>
                          <div className="flex flex-wrap gap-1">
                            {need.participant_skills.slice(0, 5).map((skill, idx) => (
                              <Badge key={`item-${idx}`} variant="outline" className="text-xs bg-sky-50">{skill}</Badge>
                            ))}
                            {need.participant_skills.length > 5 && (
                              <Badge variant="outline" className="text-xs">+{need.participant_skills.length - 5}</Badge>
                            )}
                          </div>
                        </div>
                      )}
                      {/* Participant Accomplishments */}
                      {need.participant_accomplishments?.length > 0 && (
                        <div className="mb-3">
                          <p className="text-xs font-medium text-muted-foreground mb-1.5 flex items-center gap-1">
                            <GraduationCap size={12} /> Accomplishments
                          </p>
                          <div className="flex flex-wrap gap-1">
                            {need.participant_accomplishments.slice(0, 3).map((acc, idx) => (
                              <Badge key={`item-${idx}`} variant="outline" className="text-xs bg-emerald-50">{acc}</Badge>
                            ))}
                          </div>
                        </div>
                      )}
                      {need.participant_bio && (
                        <p className="text-xs text-muted-foreground italic border-l-2 border-emerald-200 pl-2">
                          "{need.participant_bio.slice(0, 100)}{need.participant_bio.length > 100 ? '...' : ''}"
                        </p>
                      )}
                      <p className="text-xs text-muted-foreground mt-3">Posted {formatDate(need.created_at)}</p>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>

        {/* My Requests Tab */}
        <TabsContent value="requests" className="space-y-4">
          {myRequests.length === 0 ? (
            <Card className="border-0 shadow-sm rounded-2xl">
              <CardContent className="p-8 text-center">
                <MessageSquare className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
                <p className="text-muted-foreground">You haven't sent any connection requests yet.</p>
                <p className="text-sm text-muted-foreground mt-1">Browse participant needs and reach out!</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {myRequests.map(req => (
                <Card key={req.id} className="border-0 shadow-sm rounded-2xl">
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <h3 className="font-semibold">{req.need_title || 'Connection Request'}</h3>
                          {getStatusBadge(req.status)}
                        </div>
                        <p className="text-sm text-muted-foreground">To: {req.participant_name}</p>
                        {req.message && (
                          <p className="text-sm mt-2 bg-muted/30 p-2 rounded-lg">{req.message}</p>
                        )}
                        {req.resource_offered && (
                          <p className="text-sm mt-2"><span className="font-medium">Resource offered:</span> {req.resource_offered}</p>
                        )}
                        {req.approval_notes && req.status === 'rejected' && (
                          <p className="text-sm mt-2 text-red-600">Staff notes: {req.approval_notes}</p>
                        )}
                        {req.participant_response_message && (
                          <p className="text-sm mt-2 text-emerald-700">Participant response: {req.participant_response_message}</p>
                        )}
                      </div>
                      <div className="text-right text-xs text-muted-foreground">
                        <p>Sent {formatDate(req.created_at)}</p>
                        {req.approval_at && <p>Reviewed {formatDate(req.approval_at)}</p>}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Connect Dialog */}
      <Dialog open={connectDialogOpen} onOpenChange={setConnectDialogOpen}>
        <DialogContent className="rounded-2xl">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope' }}>Send Connection Request</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {selectedNeed && (
              <div className="bg-muted/30 p-3 rounded-xl">
                <p className="font-medium">{selectedNeed.title}</p>
                <p className="text-sm text-muted-foreground">{selectedNeed.description}</p>
              </div>
            )}
            <div>
              <label className="text-sm font-medium mb-1.5 block">Your Message to Participant</label>
              <DictationInput 
                value={connectMessage}
                onChange={(val) => setConnectMessage(val)}
                placeholder="Introduce yourself and how you can help..."
                className="rounded-xl"
                rows={3}
              showCleanup={false} />
            </div>
            <div>
              <label className="text-sm font-medium mb-1.5 block">Resource/Opportunity You're Offering</label>
              <Input 
                value={resourceOffered}
                onChange={(e) => setResourceOffered(e.target.value)}
                placeholder="e.g., Job opening, training program, housing assistance..."
                className="rounded-xl"
              />
            </div>
            <p className="text-xs text-muted-foreground">
              Note: Your request will be reviewed by staff before reaching the participant.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConnectDialogOpen(false)} className="rounded-full">Cancel</Button>
            <Button 
              onClick={handleConnect} 
              className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-full gap-2"
              disabled={!connectMessage.trim()}
            >
              <Send size={16} /> Send Request
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
